import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, RotateCw } from "lucide-react";

/**
 * PhotoCropDialog — Open a modal that lets the user crop the given image
 * (dataURL or blob URL) to a square 1:1 aspect and outputs a JPEG dataURL.
 *
 * Props:
 *  - open: boolean
 *  - imageSrc: string | null  (dataURL of the source photo)
 *  - onCancel(): void
 *  - onCropped(dataUrl): void
 *  - aspect?: number = 1
 *  - outputSize?: number = 512  (px, square)
 */
export default function PhotoCropDialog({
  open,
  imageSrc,
  onCancel,
  onCropped,
  aspect = 1,
  outputSize = 512,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setPixels(areaPixels);
  }, []);

  const reset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setPixels(null);
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  const handleConfirm = async () => {
    if (!pixels || !imageSrc) return;
    setBusy(true);
    try {
      const dataUrl = await getCroppedDataUrl(imageSrc, pixels, rotation, outputSize);
      onCropped?.(dataUrl);
      reset();
    } catch (e) {
      console.error("crop failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleCancel(); }}>
      <DialogContent className="sm:max-w-md" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle>Crop your photo</DialogTitle>
        </DialogHeader>
        <div className="relative w-full aspect-square bg-muted rounded-md overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid
              restrictPosition
              cropShape="rect"
            />
          )}
        </div>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs flex items-center gap-2 text-muted-foreground mb-1.5">
              <ZoomIn className="w-3.5 h-3.5" /> Zoom
            </label>
            <Slider
              value={[zoom]}
              min={1}
              max={4}
              step={0.05}
              onValueChange={(v) => setZoom(v[0])}
              data-testid="photo-crop-zoom"
            />
          </div>
          <div>
            <label className="text-xs flex items-center gap-2 text-muted-foreground mb-1.5">
              <RotateCw className="w-3.5 h-3.5" /> Rotate
            </label>
            <Slider
              value={[rotation]}
              min={0}
              max={360}
              step={1}
              onValueChange={(v) => setRotation(v[0])}
              data-testid="photo-crop-rotate"
            />
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={handleCancel} data-testid="photo-crop-cancel">Cancel</Button>
          <Button onClick={handleConfirm} disabled={busy || !pixels} data-testid="photo-crop-confirm">
            {busy ? "Processing…" : "Apply Crop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- helpers ----

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = url;
  });
}

async function getCroppedDataUrl(imageSrc, pixels, rotation = 0, outputSize = 512) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const safeArea = 2 * ((Math.max(image.width, image.height) / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(image, safeArea / 2 - image.width / 2, safeArea / 2 - image.height / 2);
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixels.width;
  canvas.height = pixels.height;
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width / 2 - pixels.x),
    Math.round(0 - safeArea / 2 + image.height / 2 - pixels.y),
  );

  // Downscale to the requested output size for portable size (JPEG ~80% quality)
  const out = document.createElement("canvas");
  out.width = outputSize;
  out.height = outputSize;
  const octx = out.getContext("2d");
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = "high";
  octx.drawImage(canvas, 0, 0, outputSize, outputSize);
  return out.toDataURL("image/jpeg", 0.85);
}
